import { SlicePipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import * as d3 from 'd3';
import { Line } from 'd3';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { InterpolationType } from 'src/app/core/enums/interpolation-type';
import { CartesianPoint } from 'src/app/core/interfaces/cartesian-point';
import { CurveCartesian } from 'src/app/core/interfaces/curve-cartesian';
import { CurvePolar } from 'src/app/core/interfaces/curve-polar';
import { ScaleType } from 'src/app/core/interfaces/plot-data';
import { DataSharingService } from 'src/app/core/services/data-sharing/data-sharing.service';
import { NumberToSciNotationPipe } from '../../pipes/number-to-sci-notation/number-to-sci-notation.pipe';

@Component({
  selector: 'app-simple-plot',
  templateUrl: './simple-plot.component.html',
  styleUrls: ['./simple-plot.component.scss'],
  providers: [NumberToSciNotationPipe, SlicePipe],
})
export class SimplePlotComponent implements AfterViewInit, OnDestroy {
  plotActivated: boolean;

  data: ({ x: number[]; y: number[] } | { r: number[]; theta: number[] })[];
  curveNames: string[];
  xLabel = 'x';
  yLabel = 'y';

  convertedData: { x: number[]; y: number[] }[];
  notablePoints: ({ x: number; y: number } | { r: number; theta: number })[];
  notablePointsNames: string[];
  interpolationType: InterpolationType;
  scaleType: ScaleType;

  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  rMax: number;

  anyIsPolar = false;

  numXTicks = 5;
  numYTicks = 5;

  svg: any = null;
  svgPointHelper: any = null;
  svgLegendContainer: any = null;
  chart: any = null;

  width = CONSTANTS.SIMPLE_PLOT_PARAMS.WIDTH;
  height = CONSTANTS.SIMPLE_PLOT_PARAMS.HEIGHT;
  pointHelperWidth = CONSTANTS.SIMPLE_PLOT_PARAMS.POINTS_HELPER_WIDTH;
  pointHelperHeight = CONSTANTS.SIMPLE_PLOT_PARAMS.POINTS_HELPER_HEIGHT;
  legendWidth = CONSTANTS.SIMPLE_PLOT_PARAMS.LEGEND_WIDTH;
  margin = CONSTANTS.SIMPLE_PLOT_PARAMS.MARGIN;

  xScale: any = null;
  yScale: any = null;
  xGrid: any = null;
  yGrid: any = null;

  divisionScalingDenominator = CONSTANTS.SIMPLE_PLOT_PARAMS.DIVISION_SCALING_DENOMINATOR;
  divisionCircleRadius = 0;
  tooltipScallingDenominator = CONSTANTS.SIMPLE_PLOT_PARAMS.TOOLTIP_SCALING_DENOMINATOR;
  tooltipFontSize: number;
  triangleSideSize = this.height / (3 * this.tooltipScallingDenominator);
  legendMaxLength = 21;
  colorPattern = CONSTANTS.SIMPLE_PLOT_PARAMS.CURVE_COLOR_PATTERN;

  interpolationTypes = {
    LINEAR_INTERPOLATION: d3.curveLinear,
    SPLINE_INTERPOLATION: d3.curveNatural,
  };

  sciNotationFactor = 12;
  boundsByNotablePoints = true;

  subscriptions: Subscription;

  constructor(
    private dataSharingService: DataSharingService,
    private numberToSciNotation: NumberToSciNotationPipe,
    private slicePipe: SlicePipe,
  ) {
    this.data = [];
    this.notablePoints = [];
  }

  ngAfterViewInit(): void {
    this.notablePoints = [];
    this.notablePointsNames = [];
    this.subscriptions = new Subscription();
    this.watchPlotChanges();
    this.watchPlotClearage();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  watchPlotChanges(): void {
    const subscription = this.dataSharingService.pointsToPlot$.subscribe((info) => {
      if (!info) {
        return;
      }

      this.boundsByNotablePoints = info.boundsByNotablePoints ?? true;
      this.plotActivated = info.plotActivated;
      this.data = info.curve;
      this.curveNames = info.curveNames;
      this.xLabel = info.xLabel;
      this.yLabel = info.yLabel;
      this.notablePoints = info.notablePoints;
      this.notablePointsNames = info.notablePointsNames;
      this.interpolationType = info.interpolationType || InterpolationType.LINEAR_INTERPOLATION;
      this.scaleType = info.scaleType || ScaleType.LINEAR_SCALE;

      if (this.plotActivated) {
        this.changePoints();
        this.addNotablePoints();
        this.drawLegends();
      } else {
        this.clearSVGElements();
      }
    });
    this.subscriptions.add(subscription);
  }

  watchPlotClearage(): void {
    const subscription = this.dataSharingService.clearPlot$.subscribe((_) => {
      this.dataSharingService.pointsToPlot$.next({
        plotActivated: false,
        curve: [],
        curveNames: [],
        xLabel: '',
        yLabel: '',
        notablePoints: [],
        notablePointsNames: [],
        interpolationType: InterpolationType.LINEAR_INTERPOLATION,
      });
    });
    this.subscriptions.add(subscription);
  }

  convertPointToCartesian(data: any): CartesianPoint {
    if (data.hasOwnProperty('x')) {
      return data as { x: number; y: number };
    }
    return {
      x: data.r * Math.cos(data.theta),
      y: data.r * Math.sin(data.theta),
    };
  }

  convertCurveToCartesian(data: CurvePolar): CurveCartesian {
    const xArray = data.r.map((r, index) => r * Math.cos(data.theta[index]));
    const yArray = data.r.map((r, index) => r * Math.sin(data.theta[index]));
    return { x: xArray, y: yArray };
  }

  convertAllToCartesian(): void {
    this.anyIsPolar = false;
    const newData = [];
    if (!this.data) {
      return;
    }
    this.data.forEach((dataArray) => {
      const isPolar = dataArray.hasOwnProperty('r');
      this.anyIsPolar = this.anyIsPolar || isPolar;
      if (isPolar) {
        const asserted = dataArray as { r: number[]; theta: number[] };
        const polarNewData = this.convertCurveToCartesian(asserted);
        newData.push(polarNewData);
      } else {
        newData.push(dataArray);
      }
    });
    this.convertedData = newData;
  }

  min(array: number[]): number {
    return Math.min(...array);
  }

  max(array: number[]): number {
    return Math.max(...array);
  }

  setSVGElements(): void {
    this.svg = d3.select('#chart');
    this.svgPointHelper = d3.select('#point-helper');
    this.svgLegendContainer = d3.select('#legends');
    this.chart = this.svg.append('g');
  }

  clearSVGElements(): void {
    this.svg?.selectAll('*').remove();
    this.svgPointHelper?.selectAll('*').remove();
    this.svgLegendContainer?.selectAll('*').remove();
  }

  setPlotBounds() {
    this.setMinAndMaxValues();

    this.svg = d3
      .select('#chart')
      .attr(CONSTANTS.VIEW_BOX, `0 0 ${this.width} ${this.height}`)
      .attr(CONSTANTS.WIDTH, '100%')
      .attr('preserveAspectRatio', 'xMinYMin meet');

    this.svgPointHelper = d3
      .select('#point-helper')
      .attr(CONSTANTS.VIEW_BOX, `0 0 100 55`)
      .attr(CONSTANTS.WIDTH, `${this.pointHelperWidth}px`)
      .attr(CONSTANTS.HEIGHT, `${this.pointHelperHeight}px`);
    this.svgPointHelper.selectAll('*').remove();

    this.svgLegendContainer = d3
      .select('#legends')
      .attr(CONSTANTS.VIEW_BOX, '0 0 0 0')
      .attr(CONSTANTS.WIDTH, `${this.legendWidth}px`)
      .attr(CONSTANTS.HEIGHT, '0px');
    this.svgLegendContainer.selectAll('*').remove();

    this.svg.selectAll('*').remove();
    this.chart = this.svg.append('g');

    if (this.anyIsPolar) {
      this.yScale = d3
        .scaleLinear()
        .range([this.height - this.margin.bottom, this.margin.top])
        .domain([-this.rMax, this.rMax]);
      this.xScale = d3
        .scaleLinear()
        .range([this.margin.left, this.width - this.margin.right])
        .domain([-this.rMax, this.rMax]);
    } else if (this.scaleType === ScaleType.LOG_SCALE) {
      this.yScale = d3
        .scaleLinear()
        .range([this.height - this.margin.bottom, this.margin.top])
        .domain([Math.min(this.yMin, 0), this.yMax]);
      this.xScale = d3
        .scaleLog()
        .range([this.margin.left, this.width - this.margin.right])
        .domain([this.xMin, this.xMax]);
    } else {
      this.yScale = d3
        .scaleLinear()
        .range([this.height - this.margin.bottom, this.margin.top])
        .domain([Math.min(this.yMin, 0), this.yMax]);
      this.xScale = d3
        .scaleLinear()
        .range([this.margin.left, this.width - this.margin.right])
        .domain([this.xMin, this.xMax]);
    }
    const widthToUse = this.width - this.margin.left - this.margin.right;
    const heightToUse = this.height - this.margin.top - this.margin.bottom;

    const possibleCircleRadiusX = widthToUse / (2 * this.divisionScalingDenominator);
    const possibleCircleRadiusY = heightToUse / (2 * this.divisionScalingDenominator);
    this.divisionCircleRadius = Math.min(possibleCircleRadiusX, possibleCircleRadiusY);

    this.tooltipFontSize = this.height / (this.tooltipScallingDenominator * 2.5);
  }

  setMinAndMaxValues(): void {
    const toDistArray = (elems: CurveCartesian) =>
      elems.x.map((currentX, index) =>
        Math.sqrt(currentX * currentX + elems.y[index] * elems.y[index]),
      );

    this.xMin = this.min(this.convertedData.map((elems) => this.min(elems.x)));
    this.xMax = this.max(this.convertedData.map((elems) => this.max(elems.x)));
    this.yMin = this.min(this.convertedData.map((elems) => this.min(elems.y)));
    this.yMax = this.max(this.convertedData.map((elems) => this.max(elems.y)));
    this.rMax = this.max(this.convertedData.map((elems) => this.max(toDistArray(elems))));
    this.setBoundsByNotablePoints();
  }

  getConvertedNotablePointsToCartesian(): Array<{ x: number; y: number }> {
    this.anyIsPolar = false;
    const newData = [];
    if (!this.data) {
      return;
    }
    this.notablePoints.forEach(
      (dataArray: { x: number; y: number } | { r: number; theta: number }) => {
        const isPolar = dataArray.hasOwnProperty('r');
        this.anyIsPolar = this.anyIsPolar || isPolar;
        if (isPolar) {
          const asserted = dataArray as { r: number; theta: number };
          const x = asserted.r * Math.cos(asserted.theta);
          const y = asserted.r * Math.sin(asserted.theta);
          newData.push({ x, y });
        } else {
          newData.push(dataArray);
        }
      },
    );
    return newData;
  }

  setBoundsByNotablePoints(): void {
    const convertedNotablePoints = this.getConvertedNotablePointsToCartesian();
    if (this.boundsByNotablePoints && this.notablePoints?.length > 0) {
      const xNotablesMax = this.max(convertedNotablePoints.map((p) => p.x));
      const xNotablesMin = this.min(convertedNotablePoints.map((p) => p.x));
      const yNotablesMax = this.max(convertedNotablePoints.map((p) => p.y));
      const yNotablesMin = this.min(convertedNotablePoints.map((p) => p.y));
      const rNotablesMax = this.max(
        convertedNotablePoints.map((p) => Math.sqrt(p.x * p.x + p.y * p.y)),
      );

      this.xMin = Math.min(this.xMin, xNotablesMin);
      this.xMax = Math.max(this.xMax, xNotablesMax);
      this.yMin = Math.min(this.yMin, yNotablesMin);
      this.yMax = Math.max(this.yMax, yNotablesMax);
      this.rMax = Math.max(this.rMax, rNotablesMax);
    }
  }

  drawLegends() {
    const sizeYBrick = 40;
    const numLegends = this.anyIsPolar
      ? this.data.length + this.notablePoints.length
      : this.notablePoints.length;
    const height = numLegends * sizeYBrick;
    const innerHeight = 100 * (height / this.legendWidth);
    const spaceForEach = innerHeight / numLegends;
    this.svgLegendContainer
      .attr(CONSTANTS.VIEW_BOX, `0 0 100 ${innerHeight}`)
      .attr(CONSTANTS.WIDTH, `${this.legendWidth}px`)
      .attr(CONSTANTS.HEIGHT, `${height}px`);
    this.svgLegendContainer.selectAll('*').remove();

    if (!numLegends) {
      return;
    }

    const legendGroup = this.svgLegendContainer
      .insert('g')
      .attr('class', 'regular-tooltip-group')
      .attr('transform', 'translate(0, 0)');

    if (this.notablePointsNames) {
      legendGroup
        .insert('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('class', 'regular-plot-tooltip')
        .style('font-size', `${this.tooltipFontSize}px`)
        .style('fill', '#244160')
        .attr(CONSTANTS.WIDTH, 95)
        .attr(CONSTANTS.HEIGHT, innerHeight);
    }

    let currentStart = spaceForEach / 2;
    if (this.anyIsPolar) {
      this.curveNames.forEach((curveName, index) => {
        const currentColor = this.colorPattern[index % this.colorPattern.length];
        legendGroup
          .insert('text')
          .attr('x', this.tooltipFontSize)
          .attr('y', currentStart)
          .style('font-size', '10px')
          .style('alignment-baseline', 'middle')
          .style('fill', currentColor)
          .text(curveName);

        currentStart += spaceForEach;
      });
    }

    this.notablePointsNames?.forEach((notablePoint, index) => {
      const tooltipId = this.getTooltipIdByPoint(notablePoint);
      const currentColor = this.colorPattern[index % this.colorPattern.length];

      const circle = legendGroup
        .insert('circle')
        .attr('cx', this.tooltipFontSize)
        .attr('cy', currentStart)
        .attr('fill', currentColor)
        .attr('r', this.divisionCircleRadius);
      const text = legendGroup
        .insert('text')
        .attr('x', 2 * this.tooltipFontSize)
        .attr('y', currentStart)
        .style('alignment-baseline', 'middle')
        .style(CONSTANTS.FONT_SIZE, '6px')
        .style('fill', currentColor)
        .text(this.truncateLegend(notablePoint))
        .on(CONSTANTS.MOUSEOVER, () => {
          this.showTooltipAndOverlay(tooltipId);
        })
        .on(CONSTANTS.MOUSEOUT, () => {
          this.hideTooltipAndOverlay(tooltipId);
        });

      if (this.notablePointsNames.length > 1 && notablePoint.length > this.legendMaxLength) {
        circle
          .on(CONSTANTS.MOUSEOVER, () => {
            this.showTooltipAndOverlay(tooltipId);
          })
          .on(CONSTANTS.MOUSEOUT, () => {
            this.hideTooltipAndOverlay(tooltipId);
          });
        text
          .on(CONSTANTS.MOUSEOVER, () => {
            this.showTooltipAndOverlay(tooltipId);
          })
          .on(CONSTANTS.MOUSEOUT, () => {
            this.hideTooltipAndOverlay(tooltipId);
          });
        this.drawTooltip(notablePoint, currentStart, index === this.notablePoints.length - 1);
      }

      currentStart += spaceForEach;
    });
  }

  getTooltipIdByPoint(notablePoint: string): string {
    return `plot-tooltip-${notablePoint.replaceAll(/[^\w\d]+/g, '-')}`;
  }

  showTooltipAndOverlay(tooltipId: string): void {
    d3.select('#' + tooltipId).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
  }

  hideTooltipAndOverlay(tooltipId: string): void {
    d3.select('#' + tooltipId).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
  }

  drawTooltip(notablePoint: string, currentStart: number, isLast: boolean): void {
    const id = this.getTooltipIdByPoint(notablePoint);
    const offset = isLast ? currentStart - 30 : currentStart + 10;
    const group = this.svgLegendContainer
      .insert('g')
      .attr('x', '0px')
      .attr('y', `${offset}px`)
      .attr(CONSTANTS.WIDTH, '95px')
      .attr(CONSTANTS.HEIGHT, '20px')
      .attr('id', id)
      .style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);

    group
      .insert('rect')
      .attr('class', 'tooltip')
      .attr('x', '0px')
      .attr('y', offset)
      .style('fill', 'black')
      .attr(CONSTANTS.WIDTH, 95)
      .attr(CONSTANTS.HEIGHT, '20px');

    group
      .insert('text')
      .attr('class', 'tooltip-text')
      .attr('x', '5px')
      .attr('y', offset + 10)
      .style('alignment-baseline', 'middle')
      .style(CONSTANTS.FONT_SIZE, '5px')
      .style('fill', 'white')
      .text(this.truncateLegend(notablePoint, 36));
  }

  truncateLegend(legend: string, maxSize = this.legendMaxLength): string {
    if (legend.length > maxSize) {
      legend = `${this.slicePipe.transform(legend, 0, maxSize - 2)}...`;
    }
    return legend;
  }

  showAxisAndGrids() {
    const fullInnerWidth = Math.max(this.width, this.height) - this.margin.left - this.margin.right;
    const innerHeight = this.height - this.margin.top - this.margin.bottom;

    if (String(this.xMax).length > this.sciNotationFactor) {
      this.xGrid = d3
        .axisBottom(this.xScale)
        .tickSize(-innerHeight)
        .tickArguments([this.numXTicks, this.numberToSciNotation.transform(this.numXTicks)]);
    } else {
      this.xGrid = d3.axisBottom(this.xScale).tickSize(-innerHeight).ticks(this.numXTicks);
    }

    if (String(this.yMax).length > this.sciNotationFactor) {
      this.yGrid = d3
        .axisLeft(this.yScale)
        .tickSize(-fullInnerWidth)
        .tickArguments([this.numYTicks, this.numberToSciNotation.transform(this.numYTicks)]);
    } else {
      this.yGrid = d3.axisLeft(this.yScale).tickSize(-fullInnerWidth).ticks(this.numYTicks);
    }

    if (!this.anyIsPolar) {
      this.chart
        .insert('g')
        .attr('transform', `translate(0, ${this.height - this.margin.bottom})`)
        .attr('class', 'axis axis--x')
        .call(this.xGrid);

      this.chart
        .insert('g')
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .attr('class', 'axis axis--y')
        .call(this.yGrid);

      this.chart
        .insert('line')
        .attr('class', 'polar-line')
        .attr('x1', this.xScale(this.xMin))
        .attr('y1', this.yScale(this.yMin))
        .attr('x2', this.xScale(this.xMax))
        .attr('y2', this.yScale(this.yMax));
    } else {
      this.chart
        .insert('g')
        .attr('transform', `translate(0, ${this.yScale(0)})`)
        .attr('class', 'axis axis--x')
        .call(d3.axisBottom(this.xScale));

      this.chart
        .insert('g')
        .attr('transform', `translate(${this.xScale(0)}, 0)`)
        .attr('class', 'axis axis--y')
        .call(d3.axisLeft(this.yScale));
    }
  }

  addPointMarkers(curve: CartesianPoint[], curveIndex, color: string) {
    const markersGroup = this.chart.insert('g');
    curve.forEach((point, pointIndex) => {
      const pointId = `p-${curveIndex.toString()}-${pointIndex.toString()}`;
      const pointTooltipId = `${pointId}-tooltip`;

      this.addToolTip(point, pointTooltipId);

      markersGroup
        .insert('circle')
        .attr('id', pointId)
        .attr('cx', this.xScale(point.x))
        .attr('cy', this.yScale(point.y))
        .attr('fill', color)
        .attr('r', this.divisionCircleRadius)
        .on('mouseover', () => {
          d3.select(`#${pointId}`).attr('r', 2 * this.divisionCircleRadius);
          d3.select(`#${pointTooltipId}`).style(CONSTANTS.VISIBILITY, CONSTANTS.VISIBLE);
        })
        .on('mouseout', () => {
          d3.select(`#${pointId}`).attr('r', this.divisionCircleRadius);
          d3.select(`#${pointTooltipId}`).style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN);
        });
    });
  }

  addNotablePoints() {
    this.notablePoints.forEach((point, index) => {
      const pointCartesian = this.convertPointToCartesian(point);
      const currentColor = this.colorPattern[index % this.colorPattern.length];
      const notableData = this.chart.insert('g');
      notableData
        .insert('circle')
        .attr('cx', this.xScale(pointCartesian.x))
        .attr('cy', this.yScale(pointCartesian.y))
        .attr('fill', currentColor)
        .attr('r', 2 * this.divisionCircleRadius);
    });
  }

  addToolTip(point: CartesianPoint, pointTooltipId: string) {
    const textX = `X: ${this.numberToSciNotation.transform(point.x)}`;
    const textY = `Y: ${this.numberToSciNotation.transform(point.y)}`;
    const textSize = Math.max(textX.length, textY.length);

    const sizeXBrick = Math.min(7, textSize) * this.tooltipFontSize;
    const sizeYBrick = this.height / this.tooltipScallingDenominator;

    const tooltipGroup = this.chart
      .insert('g')
      .attr('class', 'regular-tooltip-group')
      .attr('id', pointTooltipId)
      .style(CONSTANTS.VISIBILITY, CONSTANTS.HIDDEN)
      .attr(
        'transform',
        `translate(${this.xScale(point.x) - sizeXBrick}, ${this.yScale(point.y)})`,
      );

    tooltipGroup
      .insert('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('class', 'regular-plot-tooltip')
      .attr('width', sizeXBrick)
      .attr('height', sizeYBrick);

    tooltipGroup
      .insert('text')
      .attr('x', this.tooltipFontSize)
      .attr('y', sizeYBrick / 2)
      .style('font-size', `${this.tooltipFontSize}px`)
      .text(textX);

    tooltipGroup
      .insert('text')
      .attr('x', this.tooltipFontSize)
      .attr('y', sizeYBrick / 2 + this.tooltipFontSize)
      .style('font-size', `${this.tooltipFontSize}px`)
      .text(textY);
  }

  setCurveInterpolation(line: Line<[number, number]>): void {
    line.curve(this.interpolationTypes[this.interpolationType]);
  }

  plotCurves(): void {
    const xScale = this.xScale;
    const yScale = this.yScale;
    const line = d3
      .line()
      .x((d: any) => xScale(d.x))
      .y((d: any) => yScale(d.y));

    this.setCurveInterpolation(line);

    const lines = this.chart.insert('g');
    this.convertedData.forEach((curve, index) => {
      const points = curve.x.map((currentX, pointIdx) => ({
        x: currentX,
        y: curve.y[pointIdx],
      }));
      const currentColor = this.colorPattern[index % this.colorPattern.length];
      lines
        .insert(CONSTANTS.PATH)
        .datum(points)
        .attr('d', (d) => line(d))
        .attr('fill', 'none')
        .attr('stroke-width', '5px')
        .attr('stroke', currentColor);

      this.addPointMarkers(points, index, currentColor);
    });
  }

  setZoomProps(): void {
    const chartTotalTranslateExtent = 300;
    const chartHalfTranslateExtent = chartTotalTranslateExtent / 2;
    const zoom = d3
      .zoom()
      .scaleExtent([1, 2])
      .translateExtent([
        [-chartHalfTranslateExtent, -chartHalfTranslateExtent],
        [this.width + chartTotalTranslateExtent, this.height + chartHalfTranslateExtent],
      ]);

    this.svg.call(
      zoom.on('zoom', (e: { transform: { k: number; x: number; y: number } }) => {
        this.chart.attr('transform', e.transform);
      }),
    );
  }

  drawAxisTitles(): void {
    const legendGroup = this.chart.insert('g');

    legendGroup
      .insert('text')
      .attr('class', 'axis-title')
      .attr('x', this.width / 2 - this.yLabel.length * 2)
      .attr('y', this.height - 7)
      .style('alignment-baseline', 'middle')
      .text(this.yLabel);

    const titleX = this.margin.left / 3;
    const titleY = this.height / 2;

    legendGroup
      .insert('text')
      .attr('class', 'axis-title')
      .attr('x', titleX)
      .attr('y', titleY)
      .style('alignment-baseline', 'middle')
      .attr('transform', `rotate(-90, ${titleX}, ${titleY})`)
      .text(this.xLabel);
  }

  changePoints(): void {
    if (!this.data || !this.data?.length) {
      return;
    }
    this.convertAllToCartesian();
    this.setSVGElements();
    this.setPlotBounds();
    this.showAxisAndGrids();
    this.plotCurves();
    this.drawAxisTitles();
    this.setZoomProps();
  }
}
